import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface RideRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  rideId: string;
  driverId: string;
  customerId: string;
  driverName: string | null;
  driverAvatar: string | null;
}

const RideRatingModal = ({ isOpen, onClose, rideId, driverId, customerId, driverName, driverAvatar }: RideRatingModalProps) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({ title: "Please select a rating", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.from('ride_ratings').insert({
        ride_id: rideId,
        driver_id: driverId,
        customer_id: customerId,
        rating,
        comment: comment || null,
      });
      if (error) throw error;
      toast({ title: "Thanks for rating!", description: "Your feedback helps improve the experience." });
      onClose();
    } catch (err) {
      console.error('Rating error:', err);
      toast({ title: "Failed to submit rating", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const ratingLabels = ['', 'Terrible', 'Bad', 'Okay', 'Good', 'Excellent'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-center text-lg">How was your ride?</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-2">
          {/* Driver info */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-16 h-16 rounded-full bg-muted overflow-hidden">
              {driverAvatar ? (
                <img src={driverAvatar} alt={driverName || 'Driver'} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xl font-bold">
                  {driverName?.[0]?.toUpperCase() || 'D'}
                </div>
              )}
            </div>
            <p className="font-medium text-foreground">{driverName || 'Your Driver'}</p>
          </div>

          {/* Stars */}
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(0)}
                className="focus:outline-none transition-transform active:scale-90"
              >
                <Star
                  className={`h-10 w-10 transition-colors ${
                    star <= (hoveredStar || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-muted-foreground/30'
                  }`}
                />
              </button>
            ))}
          </div>
          {(hoveredStar || rating) > 0 && (
            <p className="text-sm font-medium text-muted-foreground">{ratingLabels[hoveredStar || rating]}</p>
          )}

          {/* Comment */}
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Leave a comment (optional)..."
            className="min-h-20 rounded-xl resize-none"
          />
        </div>

        <div className="flex gap-3 mt-2">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl" disabled={loading}>
            Skip
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || rating === 0}
            className="flex-1 rounded-xl bg-teal-500 hover:bg-teal-600 text-white"
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RideRatingModal;
